/**
 * Google Analytics 4 tracking utility
 * Provides type-safe event tracking for user actions
 */

// Declare gtag function for TypeScript
declare global {
  interface Window {
    gtag?: (
      command: 'config' | 'event' | 'set',
      targetId: string,
      config?: Record<string, any>
    ) => void;
    dataLayer?: any[];
  }
}

// Only track in production
const isProduction = process.env.NODE_ENV === 'production';

/**
 * Send a custom event to Google Analytics
 */
const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
  if (!isProduction || typeof window.gtag !== 'function') {
    return;
  }

  window.gtag('event', eventName, parameters);
};

/**
 * Analytics tracking utility
 */
export const analytics = {
  /**
   * Track page view (for SPA navigation)
   */
  pageView: (path: string, title: string) => {
    trackEvent('page_view', {
      page_path: path,
      page_title: title,
    });
  },

  /**
   * Track ferry route search
   */
  searchRoute: (params: {
    fromStop: string;
    toStop: string;
    date: string;
    resultsCount: number;
  }) => {
    trackEvent('search_route', {
      search_from: params.fromStop,
      search_to: params.toStop,
      search_date: params.date,
      results_found: params.resultsCount,
    });
  },

  /**
   * Track bookmark/trip actions
   */
  bookmarkAction: (action: 'add' | 'remove' | 'export' | 'reorder', details?: string) => {
    trackEvent('bookmark_action', {
      action_type: action,
      details: details,
    });
  },

  /**
   * Track language change
   */
  changeLanguage: (language: 'en' | 'zh') => {
    trackEvent('change_language', {
      language: language,
    });
  },

  /**
   * Track map interactions
   */
  mapInteraction: (action: 'port_click' | 'route_click' | 'zoom' | 'pan', target?: string) => {
    trackEvent('map_interaction', {
      interaction_type: action,
      target: target,
    });
  },

  /**
   * Track filter usage
   */
  useFilter: (filterType: string, value: any) => {
    trackEvent('use_filter', {
      filter_type: filterType,
      filter_value: String(value),
    });
  },

  /**
   * Track error occurrences
   */
  trackError: (errorType: string, errorMessage?: string) => {
    trackEvent('error_occurred', {
      error_type: errorType,
      error_message: errorMessage,
    });
  },

  /**
   * Track external link clicks
   */
  externalLink: (url: string, linkText: string) => {
    trackEvent('click_external_link', {
      link_url: url,
      link_text: linkText,
    });
  },

  /**
   * Track PDF downloads/views
   */
  viewPDF: (pdfName: string) => {
    trackEvent('view_pdf', {
      pdf_name: pdfName,
    });
  },

  /**
   * Track Triennale page interactions
   */
  triennaleAction: (action: 'view_schedule' | 'view_map' | 'external_link') => {
    trackEvent('triennale_interaction', {
      action_type: action,
    });
  },

  /**
   * Track custom events
   */
  custom: (eventName: string, parameters?: Record<string, any>) => {
    trackEvent(eventName, parameters);
  },
};
